// src/pages/Dashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import ConversationCard from '../components/ConversationCard';
import MessageBubble from '../components/MessageBubble';
import styles from './Dashboard.module.css';

const USE_WEBSOCKETS = process.env.REACT_APP_USE_WEBSOCKETS === 'true';
const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;

const N8N_CONVERSATIONS_URL = process.env.REACT_APP_N8N_CONVERSATIONS_URL;
const N8N_GET_MESSAGES_URL = process.env.REACT_APP_N8N_GET_MESSAGES_URL;
const N8N_SEND_MESSAGE_URL = process.env.REACT_APP_N8N_SEND_MESSAGE_URL;
//const N8N_AI_TOGGLE_URL = process.env.REACT_APP_N8N_AI_TOGGLE_URL;
const N8N_AI_TOGGLE_POST_URL = process.env.REACT_APP_N8N_POST_AITOGGLE_URL;
const N8N_AI_STATUS_GET_URL = process.env.REACT_APP_N8N_GET_AISTATUS_URL;


const SENDER_HUMAN_OPERATOR = 'human_operator';

const sortByTimestamp = (a, b) => new Date(a.received_at) - new Date(b.received_at);

const Dashboard = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [isAIActive, setIsAIActive] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [aiToggleInProgress, setAiToggleInProgress] = useState(false);

  const messagesAreaRef = useRef(null);
  const socketRef = useRef(null); // Ref for the WebSocket instance

  const currentMessages = React.useMemo(() => messages[selectedConversationId] || [], [messages, selectedConversationId]);

  // Polling logic for messages (kept as a fallback)
  const fetchMessages = useCallback((convoId) => {
    if (!convoId || !N8N_GET_MESSAGES_URL) {
      console.warn("Missing convoId or N8N_GET_MESSAGES_URL");
      return;
    }

    fetch(`${N8N_GET_MESSAGES_URL}/${convoId}`)
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`Server responded with ${res.status}`)))
      .then(serverData => {
        if (Array.isArray(serverData)) {
          const serverMessages = serverData.map(m => ({ ...m, id: String(m.id || `server-${Date.now()}-${Math.random()}`) }));

          setMessages(prevMessages => {
            const existing = prevMessages[convoId] || [];
            const optimistic = existing.filter(m => m.id.startsWith('temp-'));

            const combined = new Map();
            serverMessages.forEach(m => combined.set(m.id, m));
            optimistic.forEach(msg => {
              const isConfirmed = serverMessages.some(sm =>
                sm.content === msg.content &&
                Math.abs(new Date(sm.received_at) - new Date(msg.received_at)) < 15000
              );
              if (!isConfirmed) combined.set(msg.id, msg);
            });

            return {
              ...prevMessages,
              [convoId]: Array.from(combined.values()).sort(sortByTimestamp),
            };
          });
        }
      })
      .catch(err => console.error(`Error fetching messages for ${convoId}:`, err));
  }, []);

  // Polling logic for conversations
  const fetchConversations = useCallback((initial = false) => {
    if (!N8N_CONVERSATIONS_URL) return;

    if (initial) setLoadingConversations(true);

    fetch(N8N_CONVERSATIONS_URL)
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`Status ${res.status}`)))
      .then(data => {
        const sorted = Array.isArray(data)
          ? data.map(conv => ({ ...conv, conversation_id: String(conv.conversation_id) })) // <--- ADD THIS LINE to stringify IDs from API
            .sort((a, b) => new Date(b.last_timestamp) - new Date(a.last_timestamp))
          : []; 
        setConversations(sorted);
      })
      .catch(err => {
        console.error("Failed to fetch conversations:", err);
        if (initial) setError(err.message);
      })
      .finally(() => {
        if (initial) setLoadingConversations(false);
      });
  }, []);

  // Effect to manage conversation polling
  useEffect(() => {
    fetchConversations(true);
    const interval = setInterval(() => fetchConversations(false), 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // NEW: WebSocket logic for messages
  useEffect(() => {
    if (!USE_WEBSOCKETS || !selectedConversationId || !WS_URL) {
      // Fallback to polling or do nothing if WebSockets are disabled
      if (selectedConversationId) {
        fetchMessages(selectedConversationId);
        const pollingInterval = setInterval(() => fetchMessages(selectedConversationId), 3000);
        return () => clearInterval(pollingInterval);
      }
      return;
    }

    const socket = new WebSocket(`${WS_URL}/${selectedConversationId}`);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log('WebSocket connection established.');
      // You can send a subscription message to the server if needed
      // e.g., socket.send(JSON.stringify({ type: 'subscribe', conversationId: selectedConversationId }));
      setLoadingMessages(false);
    };

    socket.onmessage = (event) => {
      try {
        const serverMessage = JSON.parse(event.data);
        if (serverMessage && String(serverMessage.conversation_id) === String(selectedConversationId)) {
            setMessages(prevMessages => {
                const existing = prevMessages[selectedConversationId] || [];
                // --- CRITICAL FIX: Ensure incoming message has a unique 'id' property ---
                const messageWithId = {...serverMessage, id: serverMessage.id || `ws-${Date.now()}-${Math.random()}`}; // Use existing ID if present, otherwise generate a unique one
                 // --- END CRITICAL FIX ---
                // Remove optimistic updates if the server message confirms it
                const confirmedContent = messageWithId.content;
                const filtered = existing.filter(msg => 
                  !msg.id.startsWith('temp-') || msg.content !== confirmedContent
                );
                const newMessages = [...filtered, messageWithId].sort(sortByTimestamp);
                return {
                    ...prevMessages,
                    [selectedConversationId]: newMessages,
                };
            });
        }
      } catch (e) {
        console.error("Failed to parse WebSocket message:", e);
      }
    };

    socket.onclose = (event) => {
      console.log('WebSocket connection closed.', event.code, event.reason);
      // You can implement a reconnection logic here
      setLoadingMessages(false);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      setLoadingMessages(false);
    };

    // Clean up function to close the WebSocket connection
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [selectedConversationId, fetchMessages]);

  const selectConversation = useCallback((id) => {
    if (!id) return;
    setSelectedConversationId(String(id));
    setLoadingMessages(true);
    fetchMessages(String(id)); // Explicitly fetch historical messages

    if (N8N_AI_STATUS_GET_URL) {
      fetch(`${N8N_AI_STATUS_GET_URL}/${id}/status`)
        .then(res => res.ok ? res.json() : Promise.reject(new Error(`Status ${res.status}`)))
        .then(data => {
          setIsAIActive(data?.ai_active ?? true);
        })
        .catch(err => {
          console.error(`Error fetching AI status for ${id}:`, err);
          setIsAIActive(true);
        });
    } else {
      setIsAIActive(true);
    }
  }, [fetchMessages]);

  const sendMessage = useCallback(() => {
    if (!newMessage.trim() || !selectedConversationId) return;

    const optimistic = {
      id: `temp-${Date.now()}`,
      conversation_id: selectedConversationId,
      sender: SENDER_HUMAN_OPERATOR,
      content: newMessage,
      received_at: new Date().toISOString(),
    };

    setMessages(prev => ({
      ...prev,
      [selectedConversationId]: [...(prev[selectedConversationId] || []), optimistic].sort(sortByTimestamp),
    }));
    setNewMessage('');

    if (USE_WEBSOCKETS && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // Use WebSocket to send the message
      socketRef.current.send(JSON.stringify({
        content: optimistic.content,
        sender: optimistic.sender,
      }));
    } else {
      // Fallback to fetch if WebSocket is not available
      fetch(`${N8N_SEND_MESSAGE_URL}/${selectedConversationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: optimistic.content,
          sender: optimistic.sender,
        }),
      })
      .then(res => {
        if (!res.ok) {
          return res.text().then(text => {
            throw new Error(`HTTP error! status: ${res.status}, message: ${text}`);
          });
        }
        // No need to fetch messages, the WebSocket onmessage handler will handle the update
      })
      .catch(err => {
        console.error('Error sending message via fetch:', err);
        setError(`Failed to send message: ${err.message}`);
        // Revert optimistic update
        setMessages(prev => ({
          ...prev,
          [selectedConversationId]: (prev[selectedConversationId] || []).filter(m => m.id !== optimistic.id),
        }));
      });
    }
  }, [newMessage, selectedConversationId]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [currentMessages]);

  const toggleAIActive = useCallback(() => {
    if (!selectedConversationId) return;

    const newState = !isAIActive;
    setAiToggleInProgress(true);
    setIsAIActive(newState);

    fetch(`${N8N_AI_TOGGLE_POST_URL}/${selectedConversationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ai_active: newState }),
    })
      .then(res => res.ok ? res.json().catch(() => ({ ai_active: newState })) : Promise.reject(res))
      .then(data => {
        if (typeof data?.ai_active === 'boolean') {
          setIsAIActive(data.ai_active);
        }
      })
      .catch(err => {
        console.error('Error toggling AI:', err);
        setIsAIActive(!newState);
        setError('Failed to toggle AI state.');
      })
      .finally(() => setAiToggleInProgress(false));
  }, [isAIActive, selectedConversationId]);

  const currentConversationDetails = conversations.find(c => c.conversation_id === selectedConversationId);

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.sidebar}>
        <h2 className={styles.sidebarHeader}>Client Conversations</h2>
        {error && <p className={styles.errorMessage}>{error}</p>}
        {loadingConversations && <p className={styles.loadingMessage}>Loading conversations...</p>}
        {!loadingConversations && conversations.length === 0 && (
          <p className={styles.noConversationsMessage}>No conversations yet.</p>
        )}
        {conversations.map(conv => (
          <ConversationCard
            key={conv.conversation_id}
            user={conv.phone_number || 'Unknown User'}
            lastMessage={conv.last_message || ''}
            timestamp={conv.last_timestamp}
            isSelected={conv.conversation_id === selectedConversationId}
            onIntervene={() => selectConversation(conv.conversation_id)}
          />
        ))}
      </div>

      <div className={styles.chatPanel}>
        {selectedConversationId && currentConversationDetails ? (
          <>
            <div className={styles.chatHeader}>
              <h3 className={styles.chatHeaderText}>
                Chat with {currentConversationDetails.phone_number || 'Unknown'}
              </h3>
              <button
                onClick={toggleAIActive}
                className={`${styles.aiToggleButton} ${isAIActive ? styles.aiToggleButtonActive : styles.aiToggleButtonPaused}`}
                disabled={aiToggleInProgress}
              >
                {aiToggleInProgress ? (isAIActive ? 'Pausing AI...' : 'Activating AI...') : (isAIActive ? 'AI Active 🤖' : 'AI Paused ⏸️')}
              </button>
            </div>

            <div className={styles.messagesArea} ref={messagesAreaRef}>
              {loadingMessages && <p className={styles.loadingMessage}>Loading messages…</p>}
              {!loadingMessages && currentMessages.length === 0 && <p className={styles.noMessages}>No messages yet.</p>}
              {currentMessages.map(m => (
                <MessageBubble
                  key={m.id}
                  message={m.content}
                  sender={m.sender}
                  timestamp={m.received_at}
                />
              ))}
            </div>

            <div className={styles.messageInputArea}>
              <input
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type a message..."
                className={styles.messageInput}
              />
              <button
                onClick={sendMessage}
                className={styles.sendMessageButton}
                disabled={!newMessage.trim()}
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className={styles.placeholderTextContainer}>
            <p>Select a conversation to view messages.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;